import { createContext, useCallback, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export const SCROLL_LOCATION_KEY = '__scroll_key__'

export interface ScrollRoutes {
  [routeKey: string]: ScrollPositions | undefined
}

interface ScrollPositions {
  [scrollerKey: string]: number | undefined
}

export interface ScrollPositionContext {
  getData: () => ScrollRoutes
  setData: (routes: ScrollRoutes) => void
}

export const scrollPositionContext = createContext<ScrollPositionContext>({
  getData: () => ({}),
  setData: () => {},
})

export const useScrollPosition = (scrollKey: string) => {
  const ctx = useContext(scrollPositionContext)
  const location = useLocation()
  const key = location.state?.[SCROLL_LOCATION_KEY] ?? 'default'

  const getScroll = useCallback(() => {
    const data = ctx.getData()
    return data[key]?.[scrollKey]
  }, [ctx, key, scrollKey])

  const setScroll = useCallback(
    (loc: number) => {
      const oldData = ctx.getData()
      const newData = {
        ...oldData,
        [key]: {
          ...oldData[key],
          [scrollKey]: loc,
        },
      }
      ctx.setData(newData)
    },
    [ctx, key, scrollKey],
  )

  const val = useMemo(
    () => ({
      getScroll,
      setScroll,
    }),
    [getScroll, setScroll],
  )

  return val
}
